import { useState, useCallback, useRef } from 'react';
import { cellsMatchColumnId } from 'src/components/DashboardTable/utils';

interface DragState {
  isDragging: boolean;
  draggedId: string | null;
  draggedTitle: string | null; // Aggiungo il titolo della colonna trascinata
  dropZoneId: string | null;
  dragShadow: {
    visible: boolean;
    x: number;
    y: number;
    text: string;
  };
}

interface UseDragAndDropOptions {
  onColumnReorder: (newOrder: string[]) => void;
  columnOrder: string[];
  enableSound?: boolean;
  enableVibration?: boolean;
}

const findColumnOrderIndex = (order: string[], columnId: string): number => {
  const exact = order.indexOf(columnId);
  if (exact >= 0) return exact;
  // Google Ads: catalogo GAQL vs chiave logica API (segments.ad_network_type ↔ ad_network)
  return order.findIndex((id) => cellsMatchColumnId(id, columnId));
};

export const useDragAndDrop = ({
  onColumnReorder,
  columnOrder,
  enableSound = true,
  enableVibration = true,
}: UseDragAndDropOptions) => {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedId: null,
    draggedTitle: null, // Inizializzo il titolo
    dropZoneId: null,
    dragShadow: { visible: false, x: 0, y: 0, text: '' },
  });

  const audioContextRef = useRef<AudioContext | null>(null);

  const playDropSound = useCallback(() => {
    if (!enableSound) return;

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
      }

      const audioContext = audioContextRef.current;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(
        400,
        audioContext.currentTime + 0.1,
      );

      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.1,
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      console.warn('Audio feedback not supported:', error);
    }
  }, [enableSound]);

  const vibrate = useCallback(() => {
    if (!enableVibration) return;

    if ('vibrate' in navigator) {
      navigator.vibrate([50, 50, 50]);
    }
  }, [enableVibration]);

  const handleDragStart = useCallback(
    (event: React.DragEvent, draggedId: string, columnTitle?: string) => {
      event.dataTransfer.setData('text/plain', draggedId);
      event.dataTransfer.effectAllowed = 'move';

      // Uso il titolo della colonna se disponibile, altrimenti l'ID
      const displayText = columnTitle || draggedId;

      setDragState((prev) => ({
        ...prev,
        isDragging: true,
        draggedId,
        draggedTitle: columnTitle || null, // Memorizzo il titolo
        dragShadow: {
          visible: true,
          x: event.clientX + 10,
          y: event.clientY - 10,
          text: displayText, // Uso il titolo per l'ombra
        },
      }));

      // Aggiungi classe dragging al body
      document.body.classList.add('dragging');

      // Crea l'effetto di ombra personalizzato
      const dragImage = event.currentTarget.closest('th');
      if (dragImage) {
        const clone = dragImage.cloneNode(true) as HTMLElement;
        clone.style.position = 'absolute';
        clone.style.top = '-9999px';
        clone.style.left = '-9999px';
        clone.style.transform = 'rotate(5deg) scale(1.05)';
        clone.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.25)';
        clone.style.opacity = '0.9';
        document.body.appendChild(clone);
        event.dataTransfer.setDragImage(clone, 0, 0);

        setTimeout(() => {
          document.body.removeChild(clone);
        }, 0);
      }
    },
    [],
  );

  const handleDrag = useCallback((event: React.DragEvent) => {
    if (event.clientX === 0 && event.clientY === 0) return;

    setDragState((prev) => ({
      ...prev,
      dragShadow: {
        visible: true,
        x: event.clientX + 10,
        y: event.clientY - 10,
        text: prev.draggedTitle || prev.draggedId || '', // Uso il titolo invece dell'ID
      },
    }));
  }, []);

  const handleDragOver = useCallback(
    (event: React.DragEvent, targetId: string) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';

      // Uso dragState.draggedId invece di event.dataTransfer.getData che non funziona durante il drag
      const draggedId = dragState.draggedId;

      // Se stiamo trascinando una colonna diversa da quella corrente
      if (draggedId && draggedId !== targetId) {
        setDragState((prev) => ({
          ...prev,
          dropZoneId: targetId,
        }));
      } else if (draggedId === targetId) {
        // Se stiamo passando sopra la stessa colonna che stiamo trascinando, rimuovi l'effetto
        setDragState((prev) => ({
          ...prev,
          dropZoneId: null,
        }));
      }
    },
    [dragState.draggedId],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent, targetId: string) => {
      // Verifica se stiamo effettivamente uscendo dalla colonna target
      const relatedTarget = event.relatedTarget as HTMLElement;

      // Se non c'è un elemento correlato o se non è all'interno della stessa colonna
      if (
        !relatedTarget ||
        !relatedTarget.closest(`[data-column-id="${targetId}"]`)
      ) {
        // Aggiungo un piccolo delay per evitare flickering
        setTimeout(() => {
          setDragState((prev) => {
            // Solo se siamo ancora in modalità drag e la colonna corrente è ancora la drop zone
            if (prev.isDragging && prev.dropZoneId === targetId) {
              return {
                ...prev,
                dropZoneId: null,
              };
            }
            return prev;
          });
        }, 10);
      }
    },
    [],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent, targetId: string) => {
      event.preventDefault();

      const draggedId = event.dataTransfer.getData('text/plain');
      if (!draggedId || cellsMatchColumnId(draggedId, targetId)) return;

      const newOrder = [...columnOrder];
      const draggedIndex = findColumnOrderIndex(newOrder, draggedId);
      const targetIndex = findColumnOrderIndex(newOrder, targetId);
      if (draggedIndex < 0 || targetIndex < 0) return;

      const [movedId] = newOrder.splice(draggedIndex, 1);
      // Re-inserisci l'id già presente in columnOrder (chiave catalogo), non quello del drag event
      newOrder.splice(targetIndex, 0, movedId);

      onColumnReorder(newOrder);

      // Feedback sonoro e tattile
      playDropSound();
      vibrate();

      // Reset degli stati
      handleDragEnd();
    },
    [columnOrder, onColumnReorder, playDropSound, vibrate],
  );

  const handleDragEnd = useCallback(() => {
    setDragState({
      isDragging: false,
      draggedId: null,
      draggedTitle: null, // Reset anche il titolo
      dropZoneId: null,
      dragShadow: { visible: false, x: 0, y: 0, text: '' },
    });
    document.body.classList.remove('dragging');
  }, []);

  const getColumnClassName = useCallback(
    (columnId: string) => {
      const classes = [];

      // Classe per la colonna che sta venendo trascinata
      if (dragState.draggedId === columnId) {
        classes.push('dragging');
      }

      // Classe per la colonna dove si può fare il drop (effetto di illuminazione)
      if (dragState.dropZoneId === columnId && dragState.isDragging) {
        classes.push('drop-zone-hover');
      }

      // Classe aggiuntiva per tutte le colonne durante il drag (effetto sottile)
      if (dragState.isDragging && dragState.draggedId !== columnId) {
        classes.push('drag-active');
      }

      return classes.join(' ');
    },
    [dragState],
  );

  return {
    dragState,
    handleDragStart,
    handleDrag,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    getColumnClassName,
  };
};
